import { cn } from '../utils'
import { Building2, Users } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import type { Org, OrgMember, OrgRole, SentInvitation } from './types'
import { MemberList } from './MemberList'
import { InviteMemberForm } from './InviteMemberForm'
import { SentInvitationsList } from './SentInvitationsList'

export type OrgDetailProps = {
  org: Org
  members: OrgMember[]
  sentInvitations: SentInvitation[]
  currentUserRole: OrgRole | null
  onInviteMember?: (email: string) => Promise<void>
  onCancelInvitation?: (invitationId: string) => Promise<void>
  onRemoveMember?: (memberId: string) => Promise<void>
  membersLoading?: boolean
  hideHeader?: boolean
  className?: string
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const sectionVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
}

export function OrgDetail({
  org,
  members,
  sentInvitations,
  currentUserRole,
  onInviteMember,
  onCancelInvitation,
  onRemoveMember,
  membersLoading,
  hideHeader,
  className,
}: OrgDetailProps) {
  const isOwner = currentUserRole === 'owner'

  let sectionIndex = 0

  return (
    <div className={cn('space-y-6', className)}>
      {/* Org header — hidden when OrgsList is used above */}
      {!hideHeader && (
        <motion.div
          className="rounded-lg border border-border bg-card p-6"
          custom={sectionIndex++}
          variants={sectionVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="flex items-center gap-3 mb-1">
            <div className="flex-shrink-0 rounded-lg bg-primary/10 p-2">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-foreground">{org.name}</h2>
              <p className="text-xs text-muted-foreground">
                Created {formatDate(org.createdAt)}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Members */}
      <motion.div
        className="rounded-lg border border-border bg-card p-6"
        custom={sectionIndex++}
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium text-foreground">
            Members{!membersLoading && ` (${members.length})`}
          </h3>
        </div>
        {membersLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                <div className="h-7 w-7 rounded-full bg-muted/30 animate-pulse" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 w-28 rounded bg-muted/30 animate-pulse" />
                  <div className="h-3 w-40 rounded bg-muted/20 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <MemberList members={members} onRemoveMember={isOwner ? onRemoveMember : undefined} />
        )}
      </motion.div>

      {/* Owner tools */}
      {isOwner && (
        <>
          {/* Invite member */}
          <motion.div
            className="rounded-lg border border-border bg-card p-6"
            custom={sectionIndex++}
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
          >
            <h3 className="text-sm font-medium text-foreground mb-4">Invite Member</h3>
            {onInviteMember && <InviteMemberForm onSubmit={onInviteMember} />}
          </motion.div>

          {/* Sent invitations */}
          <AnimatePresence>
            {sentInvitations.length > 0 && (
              <motion.div
                className="rounded-lg border border-border bg-card p-6"
                custom={sectionIndex++}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, y: -8, transition: { duration: 0.2 } }}
              >
                <h3 className="text-sm font-medium text-foreground mb-4">
                  Sent Invitations ({sentInvitations.length})
                </h3>
                <SentInvitationsList invitations={sentInvitations} onCancelInvitation={onCancelInvitation} />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  )
}
